import { ModelConfig } from './models'
import { MergedModel } from './msq'
import { ProviderKey } from './providers'

export interface AutoChunkStrategy {
  type: 'auto'
}

export interface StaticChunkStrategy {
  type: 'static'
  static: {
    max_chunk_size_tokens: number
    chunk_overlap_tokens: number
  }
}

export type ChunkStrategies = AutoChunkStrategy | StaticChunkStrategy

export interface VectorStoreRequest {
  file_ids: string[]
  name: string
  expires_after?: any
  chunking_strategy?: ChunkStrategies
  metadata: Record<string, string>
}

export interface VectorStoreFile {
  id: string
  object: string
  created_at: number
  usage_bytes: number
  vector_store_id: string
  last_error?: any
}

export interface VectorStoreFilesResponse {
  object: string
  data: VectorStoreFile[]
}

export interface VectorStoreFileRequest {
  file_id: string
  chunking_strategy?: ChunkStrategies
}

export interface AddVectorStoreFileResponse {
  object: string
  data: VectorStoreFile
}

export interface VectorStoreFileResponse {
  object: string
  data: VectorStoreFile
}

export interface UserVectorFile {
  id: string
  bytes: number
  created_at: number
  filename: string
  path: string
}

export interface DeleteResponse {
  id: string
  object: string
  deleted: boolean
}

export interface VectorStore {
  id: string
  object: string
  created_at: number
  name: string
  bytes: number
  file_counts: {
    in_progress: number
    completed: number
    failed: number
    cancelled: number
    total: number
  }
  embeddingModel?: string
}

export interface VectorStoreWithFiles extends VectorStore {
  files: UserVectorFile[]
  embeddingModel?: string
}

export interface VectorStoreResponse {
  id: string
  object: string
  data: VectorStore
}

export interface VectorStoreListResponse {
  object: string
  data: VectorStore[]
}

// Request for POST /v1/embeddings
export interface EmbeddingRequest {
  model: string // User-configured embedding model name
  input: string | string[]
  // Add other embedding parameters if needed (e.g., encoding_format)
}

export interface SystemEmbeddingModelConfig {
  type: 'system-embedding-model'
  config: ModelConfig // Embedding models still use ModelConfig structure, but linked to a provider
}

export function isSystemEmbeddingModelConfig(config?: MergedModel): config is SystemEmbeddingModelConfig {
  return config != null && config.type === 'system-embedding-model'
}

// Removed url and apiKey from EmbeddingModelConfig
export interface EmbeddingModelConfig {
  id?: string
  timestamp?: number
  hash?: string
  name: string
  modelName: string // The actual model name used by the provider API
  providerKey: ProviderKey // Link to the provider config
}

export interface UserEmbeddingModelConfig {
  type: 'user-embedding-model'
  config: EmbeddingModelConfig
}

export function isUserEmbeddingModelConfig(config?: MergedModel): config is UserEmbeddingModelConfig {
  return config != null && config.type === 'user-embedding-model'
}

export interface UserFile {
  id: string
  object: string
  bytes: number
  created_at: number
  filename: string
  purpose: string
}

export interface ListUserFilesResponse {
  object: string
  data: UserFile[]
}

export interface AddFileRequest {
  purpose: string
}

export interface FileUpload {
  id: string
  object: string
  bytes: number
  created_at: number
  filename: string
  purpose: string
}

export interface GetFileByIdResponse {
  object: string
  data: UserFile
}

export interface EmbeddingSearchResult {
  id?: string; // Vector's ID from the search result
  text: string;
  _distance?: number;
  metadata?: Record<string, any>;
  fileName?: string;
  path?: string; // Original path of the source file
  lines?: { start: number; end: number }; // Mapped from API's 'stop' to 'end'
  collection?: string; // safeName of the collection
  model?: string; // Name of the embedding model used for the vector
}

export interface EmbeddingFile { // As defined in @missionsquad/missionsquad-core
  name: string;
  path: string;
}

export interface EmbeddingCollection { // Mirrored from @missionsquad/missionsquad-core
  name: string;
  safeName: string;
  type: string;
  source: string;
  files: EmbeddingFile[];
  chunkSize: number;
  chunkOverlap: number;
  model: string; // Embedding model identifier string
  providerKey?: ProviderKey;
  url?: string; // Will be undefined or masked if present
  apiKey?: string; // Will be undefined or masked
  created: number;
  updated: number;
  // id?: string; // The core EmbeddingCollection doesn't have a UUID id, safeName is the key.
                // The API might add it if it's combining with other data, but for now, stick to core.
}
